import { codeFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PaginationControls from '../shared/PaginationControls';
import { useUserStore } from 'store';
import { formatPriceWithCurrency, priceForDisplay } from '../../utils/vat';

/**
 * Vendor price drift review ("Toimittajahinnat muuttuneet").
 *
 * Lists catalogue-linked parts whose live catalogue price differs from the
 * price captured at sync. Applying is always user-initiated; manually edited
 * purchase prices are never flagged here, and sell prices are never touched.
 */
const PriceDriftView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  // Drift prices are ex-VAT in the API; show them the way the parts registry
  // does, or the applied price looks "wrong" next to the registry list.
  const showInclVat = currentOrganization?.default_price_includes_vat ?? false;
  const display = (value) => formatPriceWithCurrency(priceForDisplay(value, showInclVat));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const PAGE_SIZE = 50;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchDrift = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/parts/price-drift/', {
        params: { page, page_size: PAGE_SIZE },
      });
      setRows(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setSelected(new Set());
    } catch {
      setSnackbar({ open: true, message: t('catalogues.driftLoadFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    fetchDrift();
  }, [fetchDrift]);

  const toggleAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((row) => row.part_id)));
    }
  };

  const toggleOne = (partId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId);
      else next.add(partId);
      return next;
    });
  };

  const handleApply = async (all = false) => {
    setApplying(true);
    try {
      const payload = all ? { all: true } : { part_ids: Array.from(selected) };
      const response = await axios.post('/api/parts/price-drift/apply/', payload);
      setSnackbar({
        open: true,
        message: t('catalogues.driftApplied', { count: response.data.updated }),
        severity: 'success',
      });
      fetchDrift();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || t('catalogues.driftApplyFailed'),
        severity: 'error',
      });
    } finally {
      setApplying(false);
    }
  };

  const diffPercent = (row) => {
    const oldPrice = parseFloat(row.catalogue_price_at_sync);
    const newPrice = parseFloat(row.new_catalogue_price);
    if (!oldPrice) return '';
    const diff = ((newPrice - oldPrice) / oldPrice) * 100;
    return `${diff > 0 ? '+' : ''}${diff.toFixed(1)} %`;
  };

  return (
    <Box data-testid="price-drift-page">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/catalogues')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {t('catalogues.driftTitle')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          disabled={selected.size === 0 || applying}
          onClick={() => handleApply(false)}
          startIcon={applying ? <CircularProgress size={16} /> : <CheckIcon />}
          data-testid="apply-selected-button"
        >
          {t('catalogues.applySelected', { count: selected.size })}
        </Button>
        <Button
          variant="contained"
          disabled={totalCount === 0 || applying}
          onClick={() => handleApply(true)}
          data-testid="apply-all-button"
        >
          {t('catalogues.applyAll', { count: totalCount })}
        </Button>
      </Box>

      <Typography sx={{ color: 'text.secondary', mb: 2 }}>{t('catalogues.driftDescription')}</Typography>

      <Chip
        size="small"
        label={showInclVat ? t('Prices include VAT') : t('catalogues.pricesExVatNote')}
        color="info"
        variant="outlined"
        sx={{ mb: 1 }}
        data-testid="drift-vat-basis"
      />

      <Paper elevation={0} sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('catalogues.noDrift')}</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" data-testid="drift-table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={rows.length > 0 && selected.size === rows.length}
                      indeterminate={selected.size > 0 && selected.size < rows.length}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell>{t('catalogues.itemName')}</TableCell>
                  <TableCell>{t('catalogues.code')}</TableCell>
                  <TableCell>{t('catalogues.vendor')}</TableCell>
                  <TableCell align="right">{t('catalogues.currentPartPrice')}</TableCell>
                  <TableCell align="right">{t('catalogues.syncedPrice')}</TableCell>
                  <TableCell align="right">{t('catalogues.newPrice')}</TableCell>
                  <TableCell align="right">{t('catalogues.diff')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.part_id}
                    hover
                    onClick={() => toggleOne(row.part_id)}
                    sx={{ cursor: 'pointer' }}
                    data-testid={`drift-row-${row.part_id}`}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.has(row.part_id)} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.name}
                    </TableCell>
                    <TableCell sx={{ ...codeFont, fontSize: '12px' }}>{row.manufacturer_code}</TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>{row.vendor_name}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px' }}>
                      {display(row.current_price)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px', color: 'text.disabled' }}>
                      {display(row.catalogue_price_at_sync)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 600 }}>
                      {display(row.new_catalogue_price)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color:
                          parseFloat(row.new_catalogue_price) > parseFloat(row.catalogue_price_at_sync)
                            ? 'error.main'
                            : 'success.main',
                      }}
                    >
                      {diffPercent(row)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      {totalCount > PAGE_SIZE && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default PriceDriftView;
